// Merge source-review feedback and client Story comments for the editor inbox.
// Only comparable anchors in the SAME published review round may be grouped.
// Names/emails are self-reported: grouping by email is convenience, not identity verification.
export function reviewerKey(item){
 return String(item?.guest_email||'').trim().toLowerCase() ||
  String(item?.guest_name||'Anonymous reviewer').trim().toLowerCase();
}
const startIndex=note=>Number(note.first_word),endIndex=note=>Number(note.last_word);
const overlap=(a,b)=>{
 if(a.sourceId!==b.sourceId||a.version!==b.version)return false;
 const left=Math.max(a.first,b.first),right=Math.min(a.last,b.last);
 if(left>right)return false;
 const smaller=Math.min(a.last-a.first+1,b.last-b.first+1);
 return (right-left+1)/smaller>=0.6;
};
export function normaliseReviewItems(feedback=[],storyComments=[],paper={}){
 const list=feedback.map(note=>({
  ...note,recordType:'source',sourceId:String(note.source_id||''),version:note.review_version,
  first:startIndex(note),last:endIndex(note),
  reviewerKey:reviewerKey(note),
  sourceTitle:note.source_title||paper.sources?.[note.source_id]?.title||'Interview'
 }));
 const byBlock=new Map((paper.blocks||[]).map((b,i)=>[b.id,{...b,position:i+1}]));
 const otherCutBlocks=new Set((paper.assemblyCuts?.items||[])
  .filter(cut=>cut.id!==paper.assemblyCuts.activeId)
  .flatMap(cut=>(cut.snapshot?.blocks||[]).map(block=>block.id)));
 for(const note of storyComments){
  // Internal editor notes and private transcript-margin comments are not client feedback.
  if(note.author_kind!=='guest'||!note.review_version||!note.review_id)continue;
  const block=byBlock.get(note.block_id);
  // Don't mix reviewer comments from other working cuts. Still show orphaned
  // comments for deleted passages: a user may need to recover that feedback.
  if(!block&&otherCutBlocks.has(note.block_id))continue;
  list.push({...note,recordType:'story',version:note.review_version,
   reviewerKey:reviewerKey(note),sourceId:block?.sourceId||'',
   sourceTitle:paper.sources?.[block?.sourceId]?.title||'Story',
   storyPosition:block?.position||null,first:null,last:null});
 }
 return list.filter(item=>item.id&&Number.isInteger(item.version));
}
const positive=/\b(?:keep|include|retain|leave (?:it |this |that )?in|must[- ]use|don't cut|do not cut|love this)\b/i;
const negative=/\b(?:remove|cut|drop|delete|take (?:it|this|that) out|omit|don't use|do not use|avoid this)\b/i;
const negatedRemoval=/\b(?:don't|do not|never)\s+(?:cut|remove|drop|delete|omit)\b/i;
export function compareReviewerDirections(items){
 const comments=items.filter(item=>item.status==='open')
  .map(item=>({key:item.reviewerKey,positive:positive.test(item.body||''),
   negative:negative.test(item.body||'')&&!negatedRemoval.test(item.body||'')}));
 // An unqualified phrase alone is never proof of disagreement; explicitly mark as possible.
 const disagreement=comments.some(a=>a.positive && comments.some(b=>a.key!==b.key && b.negative))
  || comments.some(a=>a.negative && comments.some(b=>a.key!==b.key && b.positive));
 const aligned=comments.some(a=>a.positive && comments.some(b=>a.key!==b.key && b.positive)) ||
  comments.some(a=>a.negative && comments.some(b=>a.key!==b.key && b.negative));
 return disagreement?'possible_conflict':aligned?'similar_direction':null;
}
export function groupReviewerFeedback(items){
 const groups=[];
 const sorted=[...items].sort((a,b)=>String(a.recordType).localeCompare(String(b.recordType))||
  String(a.version).localeCompare(String(b.version))||
  String(a.sourceId).localeCompare(String(b.sourceId))||
  (a.first??0)-(b.first??0)||String(a.created_at).localeCompare(String(b.created_at)));
 for(const item of sorted){
  let group;
  if(item.recordType==='story')group=groups.find(g=>g.recordType==='story'&&g.version===item.version&&g.blockId===item.block_id);
  else if(Number.isInteger(item.first)&&Number.isInteger(item.last)){
   group=groups.find(g=>g.recordType==='source'&&g.items.every(existing=>
    overlap({sourceId:item.sourceId,version:item.version,first:item.first,last:item.last},
      {sourceId:existing.sourceId,version:existing.version,first:existing.first,last:existing.last})));
  }
  if(!group){
   group={key:item.recordType==='story'
     ? 'story:'+item.version+':'+item.block_id
     : 'source:'+item.version+':'+item.sourceId+':'+item.id,
    recordType:item.recordType,version:item.version,blockId:item.block_id||null,
    sourceId:item.sourceId,sourceTitle:item.sourceTitle,storyPosition:item.storyPosition||null,items:[]};
   groups.push(group);
  }
  group.items.push(item);
 }
 for(const g of groups){
  g.reviewers=new Set(g.items.map(reviewerKey)).size;
  g.openCount=g.items.filter(i=>i.status==='open').length;
  g.signal=g.reviewers>1?compareReviewerDirections(g.items):null;
  g.firstActivity=g.items.reduce((n,i)=>n>String(i.created_at||'')?n:String(i.created_at||''),'');
 }
 return groups.sort((a,b)=>b.openCount-a.openCount||b.reviewers-a.reviewers||
  b.firstActivity.localeCompare(a.firstActivity));
}
export function reviewerOverview(items,responses=[],version){
 const current=responses.filter(r=>r.review_version===version);
 const keys=new Set([...current.map(reviewerKey),...items.filter(i=>i.version===version).map(reviewerKey)]);
 const people=[...keys].map(key=>{
  const response=current.filter(r=>reviewerKey(r)===key)
   .sort((a,b)=>String(b.updated_at||'').localeCompare(String(a.updated_at||'')))[0];
  const feedback=items.filter(item=>item.version===version && reviewerKey(item)===key);
  const representative=response||feedback[0];
  return {key,name:representative?.guest_name||'Reviewer',email:representative?.guest_email||'',
    status:response?.status||'not_submitted',count:feedback.length};
 }).sort((a,b)=>a.name.localeCompare(b.name));
 const decided=people.filter(p=>['submitted','approved','changes_requested'].includes(p.status)).length;
 return {people,decided,awaiting:people.length-decided,
  mixedDecisions:people.some(p=>p.status==='approved')&&people.some(p=>p.status==='changes_requested')};
}
